/**
 * Horse Color Picker Component
 * Allows selecting a color for a horse with visual preview
 */
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  SelectChangeEvent,
} from "@mui/material";
import { HORSE_COLORS, HorseColor, HORSE_COLOR_HEX } from "./types";
import HorseIcon from "./HorseIcon";

interface HorseColorPickerProps {
  value: HorseColor;
  onChange: (color: HorseColor) => void;
  label?: string;
}

function HorseColorPicker({
  value,
  onChange,
  label = "Horse Color",
}: HorseColorPickerProps) {
  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value as HorseColor);
  };

  // If editing a horse saved with a legacy color, surface it as an extra
  // (deprecated) option so it displays correctly and is retained unless changed.
  const isLegacyValue = !!value && !HORSE_COLORS.includes(value as HorseColor);

  return (
    <FormControl fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select
        value={value}
        label={label}
        onChange={handleChange}
        renderValue={(selected) => (
          <Stack direction="row" alignItems="center" spacing={1}>
            <HorseIcon color={selected as HorseColor} size={24} />
            <span>{selected}</span>
          </Stack>
        )}
      >
        {isLegacyValue && (
          <MenuItem value={value}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  backgroundColor: HORSE_COLOR_HEX[value]?.face || "#8B5A2B",
                  border: "1px solid rgba(0,0,0,0.15)",
                }}
              />
              <HorseIcon color={value} size={30} />
              <span>{value} (legacy)</span>
            </Stack>
          </MenuItem>
        )}
        {HORSE_COLORS.map((color) => (
          <MenuItem key={color} value={color}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  backgroundColor: HORSE_COLOR_HEX[color]?.face || "#8B5A2B",
                  border: "1px solid rgba(0,0,0,0.15)",
                }}
              />
              <HorseIcon color={color} size={30} />
              <span>{color}</span>
            </Stack>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default HorseColorPicker;

